import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';
import { toast } from 'sonner';
import {
  Clock, Trophy, Settings, Coins, Calendar, CheckCircle2, Lock, Share2,
  Loader2, Eye, EyeOff, Shield, Users, MessageSquare, Check, X as XIcon,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

/* ═══════════════════════════════════════════════
   RECOVERY CLOCK
   ═══════════════════════════════════════════════ */
function RecoveryClock({ cleanDate, duration: serverDuration }) {
  const { t } = useTranslation();
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!cleanDate) { setTime(serverDuration || { days: 0, hours: 0, minutes: 0, seconds: 0 }); return; }
    const cd = new Date(cleanDate);
    const tick = () => {
      const diff = Math.max(0, Math.floor((Date.now() - cd.getTime()) / 1000));
      setTime({
        days: Math.floor(diff / 86400),
        hours: Math.floor((diff % 86400) / 3600),
        minutes: Math.floor((diff % 3600) / 60),
        seconds: diff % 60,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cleanDate, serverDuration]);

  const units = [
    { value: time.days, label: t('dashboard.days') },
    { value: time.hours, label: t('dashboard.hours') },
    { value: time.minutes, label: t('dashboard.min') },
    { value: time.seconds, label: t('dashboard.sec') },
  ];

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3" data-testid="recovery-clock">
      {units.map((u, i) => (
        <div key={i} className="text-center">
          <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-2xl bg-gradient-to-b from-zinc-800 to-zinc-900 border border-zinc-700/50 flex items-center justify-center shadow-lg">
            <span className="text-2xl sm:text-3xl font-black tabular-nums text-white">
              {String(u.value).padStart(u.value > 99 ? 1 : 2, '0')}
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 font-medium">{u.label}</p>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MEDAL CARD
   ═══════════════════════════════════════════════ */
function MedalCard({ medal, earned, current, onShare }) {
  return (
    <div className={`relative group flex flex-col items-center gap-2 ${!earned ? 'opacity-40' : ''}`} data-testid={`medal-${medal.key}`}>
      {current && <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 border-2 border-background animate-pulse z-10" />}
      <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 transition-all ${earned ? (current ? 'border-amber-500 shadow-lg shadow-amber-500/20' : 'border-zinc-600') : 'border-zinc-800'}`}>
        <img src={medal.image} alt={medal.title_pt} className={`w-full h-full object-cover ${!earned ? 'grayscale' : ''}`} loading="lazy" />
      </div>
      <div className="text-center">
        <p className={`text-xs font-bold ${current ? 'text-amber-400' : earned ? 'text-foreground' : 'text-muted-foreground'}`}>{medal.title_pt}</p>
        <p className="text-[10px] text-muted-foreground">{medal.days === 0 ? '0 dias' : `${medal.days} dias`}</p>
        {earned && medal.earned_at && (
          <p className="text-[9px] text-muted-foreground/60">{new Date(medal.earned_at).toLocaleDateString('pt-BR')}</p>
        )}
        {!earned && <Lock className="h-3 w-3 text-muted-foreground/40 mx-auto mt-0.5" />}
      </div>
      {earned && onShare && (
        <Button variant="ghost" size="sm" onClick={() => onShare(medal)} className="opacity-0 group-hover:opacity-100 transition-opacity h-6 text-[10px] text-muted-foreground hover:text-amber-400" data-testid={`share-${medal.key}`}>
          <Share2 className="h-3 w-3 mr-1" /> Compartilhar
        </Button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PROGRESS BAR
   ═══════════════════════════════════════════════ */
function ProgressBar({ progress, nextMedal }) {
  const { t } = useTranslation();
  return (
    <div data-testid="progress-bar">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-muted-foreground">{t('dashboard.nextMedal')}</span>
        <span className="text-xs font-bold text-amber-400">{nextMedal ? (t(`medals.${nextMedal.key}`) || nextMedal.title_pt) : t('dashboard.complete')}</span>
      </div>
      <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-1000 ease-out" style={{ width: `${progress}%` }} />
      </div>
      <p className="text-[10px] text-muted-foreground text-right mt-1">{progress}%</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CELEBRATION MODAL (with confetti + ARIA)
   ═══════════════════════════════════════════════ */
function CelebrationModal({ medal, open, onClose, onShare }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (open && medal) {
      // Fire confetti
      const duration = 3000;
      const end = Date.now() + duration;
      const frame = () => {
        confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#f59e0b', '#d946ef', '#06b6d4'] });
        confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#f59e0b', '#d946ef', '#06b6d4'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [open, medal]);

  if (!medal) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md text-center" data-testid="celebration-modal">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">{t('celebration.title')}</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {/* ARIA avatar */}
          <div className="flex justify-center gap-3 mb-2">
            <img src="/aria/feminino.png" alt="ARIA" className="w-14 h-14 rounded-full border-2 border-purple-500/30 object-cover" />
          </div>
          <p className="text-xs text-muted-foreground italic">ARIA: &ldquo;{t('celebration.ariaMsg')}&rdquo;</p>

          {/* Medal */}
          <div className="w-28 h-28 rounded-full overflow-hidden mx-auto border-4 border-amber-500 shadow-2xl shadow-amber-500/30">
            <img src={medal.image} alt={medal.title_pt} className="w-full h-full object-cover" />
          </div>
          <h3 className="text-2xl font-black text-amber-400">{t(`medals.${medal.key}`) || medal.title_pt}</h3>
          <p className="text-sm text-muted-foreground">
            {t('celebration.congrats')} <strong className="text-foreground">{t(`medals.${medal.key}`) || medal.title_pt}</strong>!
          </p>
          <Badge className="bg-amber-500/10 text-amber-400 text-sm px-4 py-1.5">
            <Coins className="h-4 w-4 mr-1.5" /> +{medal.awa} AWA
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">{t('celebration.close')}</Button>
          <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" onClick={() => { onShare(medal); onClose(); }} data-testid="celebration-share-btn">
            <Share2 className="h-4 w-4 mr-1.5" /> {t('celebration.share')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════
   SHARE MODAL
   ═══════════════════════════════════════════════ */
function ShareModal({ medal, totalDays, open, onClose }) {
  const [text, setText] = useState('');
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (medal && totalDays) {
      setText(`Hoje completei ${totalDays} dias limpo(a). Gratidão ao meu Jardim e à comunidade U-Clean. 🏅 ${medal.title_pt}`);
    }
  }, [medal, totalDays]);

  const handleShare = async () => {
    if (!medal) return;
    setSharing(true);
    try {
      await axios.post(`${API}/api/dashboard/share-medal`, { medal_key: medal.key, text }, { headers: headers() });
      toast.success('Compartilhado no Feed!');
      onClose();
    } catch { toast.error('Erro ao compartilhar'); }
    finally { setSharing(false); }
  };

  if (!medal) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle>Compartilhar Medalha</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-amber-500 flex-shrink-0">
              <img src={medal.image} alt="" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="font-bold text-sm">{medal.title_pt}</p>
              <p className="text-xs text-muted-foreground">{medal.days} dias</p>
            </div>
          </div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Escreva algo..." className="resize-none" data-testid="share-text" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
          <Button onClick={handleShare} disabled={sharing} className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" data-testid="share-submit">
            {sharing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Share2 className="h-4 w-4 mr-1" />}
            Publicar no Feed
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════
   CONFIRM CLEAN DATE
   ═══════════════════════════════════════════════ */
function ConfirmDateFlow({ estimatedDate, onConfirm, loading }) {
  const [date, setDate] = useState('');

  useEffect(() => {
    if (estimatedDate) {
      const d = new Date(estimatedDate);
      setDate(d.toISOString().split('T')[0]);
    }
  }, [estimatedDate]);

  return (
    <Card className="rounded-2xl border-amber-500/20 bg-amber-500/5" data-testid="confirm-date-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <Calendar className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-bold text-sm">Confirme sua Data de Reinício</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Confirme ou corrija a data para ativar o relógio e medalhas.
            </p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs">Data de reinício (clean date)</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} max={new Date().toISOString().split('T')[0]} className="mt-1" data-testid="clean-date-input" />
          </div>
          <Button onClick={() => onConfirm(date)} disabled={!date || loading} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="confirm-date-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Confirmar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════════════════════
   MENTORSHIP SECTION
   ═══════════════════════════════════════════════ */
function MentorshipSection() {
  const [requests, setRequests] = useState({ received: [], sent: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRequests(); }, []);

  const loadRequests = async () => {
    try {
      const res = await axios.get(`${API}/api/dashboard/apadrinhamento/requests`, { headers: headers() });
      setRequests(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleAction = async (id, status) => {
    try {
      await axios.patch(`${API}/api/dashboard/apadrinhamento/${id}`, { status }, { headers: headers() });
      toast.success(status === 'accepted' ? 'Apadrinhamento aceito!' : 'Solicitação recusada');
      loadRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />;

  const pending = requests.received.filter(r => r.status === 'pending');
  const active = requests.received.filter(r => r.status === 'accepted');

  return (
    <div className="space-y-4" data-testid="mentorship-section">
      <h4 className="text-sm font-bold flex items-center gap-2"><Users className="h-4 w-4 text-amber-500" /> Apadrinhamento</h4>

      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Solicitações pendentes:</p>
          {pending.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
              <div className="w-8 h-8 rounded-full bg-zinc-700 overflow-hidden flex-shrink-0">
                {r.user_avatar && <img src={r.user_avatar} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{r.user_name}</p>
                {r.message && <p className="text-[10px] text-muted-foreground truncate">{r.message}</p>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" onClick={() => handleAction(r.id, 'accepted')} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Check className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleAction(r.id, 'rejected')} className="h-7 px-2 text-red-400 hover:text-red-300">
                  <XIcon className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {active.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Afilhados ativos:</p>
          {active.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <div className="w-7 h-7 rounded-full bg-zinc-700 overflow-hidden flex-shrink-0">
                {r.user_avatar && <img src={r.user_avatar} alt="" className="w-full h-full object-cover" />}
              </div>
              <p className="text-xs font-medium">{r.user_name}</p>
              <Badge className="bg-emerald-500/10 text-emerald-400 text-[10px] ml-auto">Ativo</Badge>
            </div>
          ))}
        </div>
      )}

      {pending.length === 0 && active.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-3">Nenhuma solicitação de apadrinhamento.</p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN DASHBOARD PAGE
   ═══════════════════════════════════════════════ */
export default function DashboardPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/dashboard`, { headers: headers() });
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConfirmDate = async (dateStr) => {
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/api/dashboard/confirm-clean-date`, {
        clean_date: new Date(dateStr).toISOString(),
      }, { headers: headers() });

      if (res.data.new_medals?.length > 0) {
        const lastKey = res.data.new_medals[res.data.new_medals.length - 1];
        const allMedals = data?.medals || [];
        const medalDef = allMedals.find(m => m.key === lastKey);
        if (medalDef) setCelebration(medalDef);
      }

      toast.success(`Data confirmada! ${res.data.medals_granted} medalha(s) concedida(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar data');
    } finally { setConfirming(false); }
  };

  const handlePrivacy = async (key, value) => {
    try {
      await axios.patch(`${API}/api/dashboard/privacy`, { [key]: value }, { headers: headers() });
      load();
      toast.success('Configuração atualizada');
    } catch { toast.error('Erro'); }
  };

  const handleShareFromCelebration = (medal) => {
    setShareTarget(medal);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  const d = data || {};
  const currentMedal = d.current_medal;
  const medals = d.medals || [];

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-black">{t('dashboard.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>

        {/* Confirm date if needed */}
        {d.needs_confirmation && !d.clean_date && (
          <FadeInItem index={0}>
            <ConfirmDateFlow estimatedDate={d.estimated_date} onConfirm={handleConfirmDate} loading={confirming} />
          </FadeInItem>
        )}

        {/* Clock */}
        <FadeInItem index={1}>
          <Card className="rounded-2xl border-border/40 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 dark:from-zinc-900 dark:to-zinc-950 p-6 sm:p-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Clock className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-widest">{t('dashboard.cleanTime')}</span>
              </div>
              <RecoveryClock cleanDate={d.clean_date} duration={d.duration} />

              {/* Current Medal */}
              {currentMedal && (
                <div className="flex items-center justify-center gap-3 mt-6 pt-5 border-t border-zinc-800">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-amber-500">
                    <img src={currentMedal.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-400">{t(`medals.${currentMedal.key}`) || currentMedal.title_pt}</p>
                    <p className="text-[10px] text-zinc-500">{t('dashboard.currentLevel')}</p>
                  </div>
                  {d.is_mentor_eligible && (
                    <Badge className="bg-purple-500/10 text-purple-400 text-[10px] ml-2">
                      <Shield className="h-3 w-3 mr-1" /> Mentor
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {d.next_medal && (
              <CardContent className="p-5">
                <ProgressBar progress={d.progress_to_next} nextMedal={d.next_medal} />
              </CardContent>
            )}
          </Card>
        </FadeInItem>

        {/* Set date if none */}
        {!d.clean_date && !d.needs_confirmation && (
          <FadeInItem index={2}>
            <ConfirmDateFlow estimatedDate={null} onConfirm={handleConfirmDate} loading={confirming} />
          </FadeInItem>
        )}

        {/* Tabs */}
        <FadeInItem index={3}>
          <Tabs defaultValue="gallery" className="w-full">
            <TabsList className="grid w-full grid-cols-3 rounded-xl">
              <TabsTrigger value="gallery" className="flex items-center gap-1.5 rounded-lg text-xs" data-testid="tab-gallery">
                <Trophy className="h-3.5 w-3.5" /> {t('dashboard.gallery')}
              </TabsTrigger>
              <TabsTrigger value="rewards" className="flex items-center gap-1.5 rounded-lg text-xs" data-testid="tab-rewards">
                <Coins className="h-3.5 w-3.5" /> {t('dashboard.rewards')}
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-1.5 rounded-lg text-xs" data-testid="tab-settings">
                <Settings className="h-3.5 w-3.5" /> {t('dashboard.settings')}
              </TabsTrigger>
            </TabsList>

            {/* Gallery */}
            <TabsContent value="gallery">
              <Card className="rounded-2xl border-border/40 shadow-sm">
                <CardContent className="p-5">
                  <h3 className="font-bold text-sm mb-4">{t('dashboard.galleryTitle')}</h3>
                  <div className="grid grid-cols-4 gap-4">
                    {medals.map((m) => (
                      <MedalCard
                        key={m.key}
                        medal={m}
                        earned={m.earned}
                        current={currentMedal?.key === m.key}
                        onShare={m.earned ? setShareTarget : null}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Rewards */}
            <TabsContent value="rewards">
              <Card className="rounded-2xl border-border/40 shadow-sm">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-sm">{t('dashboard.rewardsTitle')}</h3>
                    <Badge className="bg-amber-500/10 text-amber-400 text-sm px-3 py-1">
                      <Coins className="h-3.5 w-3.5 mr-1" /> {d.rewards_balance || 0} AWA
                    </Badge>
                  </div>

                  {(d.rewards_history || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">{t('dashboard.noRewards')}</p>
                  ) : (
                    <div className="space-y-2">
                      {(d.rewards_history || []).map((r, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                          <div className="flex items-center gap-2">
                            <Coins className="h-3.5 w-3.5 text-amber-500" />
                            <span className="text-xs">{r.reason}</span>
                          </div>
                          <span className="text-xs font-bold text-amber-400">+{r.amount} AWA</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-[10px] text-muted-foreground text-center pt-2 border-t border-border/30">
                    {t('dashboard.rewardsFuture')}
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Settings */}
            <TabsContent value="settings">
              <Card className="rounded-2xl border-border/40 shadow-sm">
                <CardContent className="p-5 space-y-5">
                  <h3 className="font-bold text-sm">{t('dashboard.settingsTitle')}</h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {d.privacy?.visibility === 'public' ? <Eye className="h-4 w-4 text-emerald-500" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                        <div>
                          <p className="text-sm font-medium">{t('dashboard.publicJourney')}</p>
                          <p className="text-[10px] text-muted-foreground">{t('dashboard.publicJourneyDesc')}</p>
                        </div>
                      </div>
                      <Switch checked={d.privacy?.visibility === 'public'} onCheckedChange={(v) => handlePrivacy('visibility', v ? 'public' : 'private')} data-testid="toggle-visibility" />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{t('dashboard.showTime')}</p>
                          <p className="text-[10px] text-muted-foreground">{t('dashboard.showTimeDesc')}</p>
                        </div>
                      </div>
                      <Switch checked={d.privacy?.show_time !== false} onCheckedChange={(v) => handlePrivacy('show_time', v)} data-testid="toggle-show-time" />
                    </div>

                    {d.is_mentor_eligible && (
                      <div className="flex items-center justify-between pt-3 border-t border-border/30">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-amber-500" />
                          <div>
                            <p className="text-sm font-medium">{t('dashboard.mentorAvailable')}</p>
                            <p className="text-[10px] text-muted-foreground">{t('dashboard.mentorBadge')}</p>
                          </div>
                        </div>
                        <Switch checked={d.privacy?.allow_mentorship === true} onCheckedChange={(v) => handlePrivacy('allow_mentorship', v)} data-testid="toggle-mentorship" />
                      </div>
                    )}
                  </div>

                  {/* Mentorship section */}
                  {d.is_mentor_eligible && d.privacy?.allow_mentorship && (
                    <div className="pt-4 border-t border-border/30">
                      <MentorshipSection />
                    </div>
                  )}

                  {/* Edit clean date */}
                  <div className="pt-4 border-t border-border/30">
                    <ConfirmDateFlow estimatedDate={d.clean_date} onConfirm={handleConfirmDate} loading={confirming} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </FadeInItem>

        {/* Modals */}
        <CelebrationModal medal={celebration} open={!!celebration} onClose={() => setCelebration(null)} onShare={handleShareFromCelebration} />
        <ShareModal medal={shareTarget} totalDays={d.duration?.total_days} open={!!shareTarget} onClose={() => setShareTarget(null)} />
      </div>
    </PageTransition>
  );
}
